/**
 * Workflow History API Service
 * Provides methods to interact with workflow execution history endpoints
 */

class WorkflowHistory {
  constructor(workflowApi) {
    this.api = workflowApi;
  }

  /**
   * Fetch paginated workflow execution history
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of records to return (max: 100)
   * @param {number} params.offset - Number of records to skip
   * @param {string} params.status - Filter by status: completed, failed, timeout
   * @param {string} params.ref - Filter by ref (workflow_uuid or component_uuid)
   * @param {string} params.type - Filter by type: project or component
   * @param {string} params.workflow_name - Filter by workflow name (partial match, case-insensitive)
   * @param {string} params.context_uuid - Filter by context UUID
   * @returns {Promise<Array>} Array of workflow execution history records
   */
  async fetchHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
      if (params.status) queryParams.append('status', params.status);
      if (params.ref) queryParams.append('ref', params.ref);
      if (params.type) queryParams.append('type', params.type);
      if (params.workflow_name) queryParams.append('workflow_name', params.workflow_name);
      if (params.context_uuid) queryParams.append('context_uuid', params.context_uuid);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      
      const url = `/workflows/history${queryParams.toString() ? `?${queryParams}` : ''}`;
      
      const response = await this.api.get(url);
      const body = response.data;

      // API now returns { data: [{ context_uuid, executions }], limit, offset, total }
      return {
        groups: body.data || [],
        total: body.total || 0,
        limit: body.limit || params.limit || 20,
        offset: body.offset || 0
      };
    } catch (error) {
      console.error('Failed to fetch workflow history:', error);
      throw error;
    }
  }

  /**
   * Fetch currently running workflow executions
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of records to return (max: 100)
   * @param {number} params.offset - Number of records to skip
   * @param {string} params.status - Filter by status: running or paused
   * @param {string} params.ref - Filter by ref (workflow_uuid or component_uuid)
   * @param {string} params.type - Filter by type: project or component
   * @param {string} params.workflow_name - Filter by workflow name (partial match, case-insensitive)
   * @param {string} params.context_uuid - Filter by context UUID
   * @returns {Promise<Object>} Object with data (flat array of executions), total, limit, offset
   */
  async fetchRunningWorkflows(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
      if (params.status) queryParams.append('status', params.status);
      if (params.ref) queryParams.append('ref', params.ref);
      if (params.type) queryParams.append('type', params.type);
      if (params.workflow_name) queryParams.append('workflow_name', params.workflow_name);
      if (params.context_uuid) queryParams.append('context_uuid', params.context_uuid);
      
      const url = `/workflows/running${queryParams.toString() ? `?${queryParams}` : ''}`;
      
      const response = await this.api.get(url);
      const body = response.data;

      // API returns flat array: { data: [execution1, execution2, ...], limit, offset, total }
      return {
        data: body.data || [],
        total: body.total || 0,
        limit: body.limit || params.limit || 20,
        offset: body.offset || 0
      };
    } catch (error) {
      console.error('Failed to fetch running workflows:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed execution information for a specific workflow
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Detailed execution information
   */
  async fetchExecutionDetails(executionUuid) {
    try {
      const response = await this.api.get(`/workflows/history/${executionUuid}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch execution details:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed information for a specific running workflow
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Detailed running workflow information
   */
  async fetchRunningWorkflowDetails(executionUuid) {
    try {
      const response = await this.api.get(`/workflows/running/${executionUuid}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch running workflow details:', error);
      throw error;
    }
  }

  /**
   * Fetch execution details from either running or history based on availability
   * Tries running endpoint first, then falls back to history
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Detailed execution information
   */
  async fetchExecutionDetailsAuto(executionUuid) {
    try {
      // Try running endpoint first (for active workflows)
      return await this.fetchRunningWorkflowDetails(executionUuid);
    } catch (error) {
      // If not found in running, try history (for completed workflows)
      if (error.response && error.response.status === 404) {
        return await this.fetchExecutionDetails(executionUuid);
      }
      throw error;
    }
  }

  /**
   * Fetch aggregated workflow execution statistics
   * @param {number} days - Number of days to analyze (1-365)
   * @returns {Promise<Object>} Workflow statistics
   */
  async fetchStats(days = 7) {
    try {
      const response = await this.api.get(`/workflows/stats?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch workflow stats:', error);
      throw error;
    }
  }

  /**
   * Pause a running workflow
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Success response
   */
  async pauseWorkflow(executionUuid) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/pause`);
      return response.data;
    } catch (error) {
      console.error('Failed to pause workflow:', error);
      throw error;
    }
  }

  /**
   * Resume a paused workflow
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Success response
   */
  async resumeWorkflow(executionUuid) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/resume`);
      return response.data;
    } catch (error) {
      console.error('Failed to resume workflow:', error);
      throw error;
    }
  }

  /**
   * Kill (terminate) a running or paused workflow
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Success response
   */
  async killWorkflow(executionUuid) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/kill`);
      return response.data;
    } catch (error) {
      console.error('Failed to kill workflow:', error);
      throw error;
    }
  }

  /**
   * Pause a specific node in a workflow
   * @param {string} executionUuid - The unique execution identifier
   * @param {number} nodeId - The unique node identifier
   * @returns {Promise<Object>} Success response
   */
  async pauseNode(executionUuid, nodeId) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/nodes/${nodeId}/pause`);
      return response.data;
    } catch (error) {
      console.error('Failed to pause node:', error);
      throw error;
    }
  }

  /**
   * Resume a paused node in a workflow
   * @param {string} executionUuid - The unique execution identifier
   * @param {number} nodeId - The unique node identifier
   * @returns {Promise<Object>} Success response
   */
  async resumeNode(executionUuid, nodeId) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/nodes/${nodeId}/resume`);
      return response.data;
    } catch (error) {
      console.error('Failed to resume node:', error);
      throw error;
    }
  }

  /**
   * Kill (terminate) a specific node in a workflow
   * @param {string} executionUuid - The unique execution identifier
   * @param {number} nodeId - The unique node identifier
   * @returns {Promise<Object>} Success response
   */
  async killNode(executionUuid, nodeId) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/nodes/${nodeId}/kill`);
      return response.data;
    } catch (error) {
      console.error('Failed to kill node:', error);
      throw error;
    }
  }

  /**
   * Restart a workflow with smart mode (preserving completed nodes)
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Success response
   */
  async restartWorkflowSmart(executionUuid) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/restart`);
      return response.data;
    } catch (error) {
      console.error('Failed to restart workflow (smart):', error);
      throw error;
    }
  }

  /**
   * Restart a workflow with fresh mode (clearing all node states)
   * @param {string} executionUuid - The unique execution identifier
   * @returns {Promise<Object>} Success response
   */
  async restartWorkflowFresh(executionUuid) {
    try {
      const response = await this.api.post(`/workflows/${executionUuid}/restart?clear_state=true`);
      return response.data;
    } catch (error) {
      console.error('Failed to restart workflow (fresh):', error);
      throw error;
    }
  }
}

export default WorkflowHistory;
